import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import CreditAccount from 'app/src/data/models/CreditAccount';
import PaymentType from 'app/src/data/models/PaymentType';
import Localize from 'app/src/localize/Localize';
import DepositPayment from 'app/src/payments/DepositPayment';
import GiftCardManager from 'app/src/payments/GiftCardManager';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleCalculationHelper from 'app/src/payments/SaleCalculationHelper';
import SaleManager from 'app/src/payments/SaleManager';
import PaymentGiftCardView from 'app/src/views/payments/PaymentGiftCardView';

const _Events = {
  GIFT_CARD_PAYMENT_FINISHED: 'gift:card:payment:finished',
};

export default class PaymentGiftCardViewController extends BasePaymentController {
  static get Event() {
    return _Events;
  }

  /**
   * Constructor for a PaymentScanGiftCardViewController
   *
   * @param saleManager {SaleManager}     The sale manager for the sale
   * @param giftCardModel {CreditAccount}     The gift card model
   * @param giftCardsInfo {array}  Gift cards with attached customer name
   * @param options {Object}  Controller options
   */
  constructor(saleManager, giftCardModel, giftCardsInfo, options = {}) {
    if (!(giftCardModel instanceof CreditAccount && giftCardModel.get('giftCard') === true)) {
      throw 'A gift card model must be specified.';
    }

    super(saleManager);

    this._giftCardModel = giftCardModel;
    this._options = options;

    // Don't show minimum refund if this is the only available Payment Type
    // If we're not doing a refund sale, this variable is not used.
    let showMinimumRefund = true;
    if (this._saleManager.getRefundablePaymentTypes().length === 1) {
      showMinimumRefund = false;
    }

    const showBackButton = giftCardsInfo.length;

    // initialize the controller's managed view
    this.view = new PaymentGiftCardView({
      model: giftCardModel,
      viewData: {
        isRefundSale: this._saleManager.isRefundSale(),
        minimumRefund: GiftCardManager.getMinimumRefund(this._saleManager),
        showMinimumRefund,
        options,
        giftCardsInfo,
        showBackButton,
      },
    });
  }

  /**
   * Initializes the state of dynamic data within the view
   */
  setInitialViewState() {
    let paymentAmount = this._saleManager.pendingBalance;
    if (this._options.isDepositPayment) {
      paymentAmount = this._options.paymentAmount;
    }

    if (!this._saleManager.isRefundSale()) {
      paymentAmount = Decimal.min(
        paymentAmount.abs(),
        this._giftCardModel.availableCreditBalance.abs()
      );
    } else {
      paymentAmount = SaleCalculationHelper.calculateMaxRefundForPaymentType(
        this._saleManager,
        GiftCardManager.getGiftCardPaymentTypeID()
      );
    }

    paymentAmount = paymentAmount.abs();

    this.view.setPaymentAmount(paymentAmount);
  }

  /**
   * Event handler for when the user has modifies the input field
   *
   * @param amount {Decimal}   The inputted amount
   */
  onGiftCardPaymentChanged(amount) {
    amount = Format.parseDecimal(amount, new Decimal(0));

    if (this._saleManager.isRefundSale()) {
      amount = amount.abs().negated();
      let maxRefundAmount = SaleCalculationHelper.calculateMaxRefundForPaymentType(
        this._saleManager,
        GiftCardManager.getGiftCardPaymentTypeID()
      );
      if (amount.isLessThan(maxRefundAmount)) {
        amount = maxRefundAmount;
      }
    }

    this.view.setPaymentAmount(amount.abs());
  }

  /**
   * Event handler for when the user has requested to make a gift card payment.
   *
   * @param amount {Decimal}   The requested gift card payment
   */
  onGiftCardPaymentRequested(amount) {
    if (
      !this._saleManager.isRefundSale() &&
      amount.isGreaterThan(this._giftCardModel.availableCreditBalance)
    ) {
      alertUser(
        Localize.translate(
          'Gift card charge can not be larger than the available balance on the card.'
        )
      );

      this.view.setPaymentAmount(this._giftCardModel.availableCreditBalance);
    } else if (amount.isZero()) {
      alertUser(Localize.translate('An invalid amount has been entered.'));
    } else {
      let paymentAmount = amount;
      if (this._saleManager.isRefundSale() && !paymentAmount.isNegative()) {
        // refunds are negative amounts
        paymentAmount = paymentAmount.negated();
      }

      // create the payment and validate before registering the payment with the sale manager
      let giftCardPayment = PaymentFactory.createPayment(
        PaymentTypeManager.Types.GIFT_CARD,
        paymentAmount,
        this._giftCardModel.id
      );

      // if the payment is for a deposit then create a deposit payment with the gift card payment
      let promise;
      if (this._options.isDepositPayment) {
        promise = this._saleManager
          .addDeposit(
            PaymentFactory.createPayment(
              PaymentTypeManager.Types.DEPOSIT,
              giftCardPayment,
              this._saleManager.creditAccountID
            )
          )
          .done(() => {
            flashNotifier(
              'success',
              Localize.translate(
                `Credit Account Deposit Of <var>${Format.formatMoney(paymentAmount)}</var> Added.`
              ),
              4
            );
          });
      } else {
        let validationResult = this._saleManager.validatePayment(giftCardPayment);
        if (validationResult.error === SaleManager.Errors.NO_ERROR) {
          promise = this._saleManager.addPayment(giftCardPayment);
        } else if (validationResult.error === SaleManager.Errors.USER_FACING) {
          alertUser(validationResult.msg);
        } else {
          alertUser(
            Localize.translate('An unexpected error occurred trying to add the gift card payment.')
          );
        }
      }

      if (promise) {
        promise
          .fail(() => {
            alertUser(Localize.translate('Could not add the gift card payment to the sale.'));
          })
          .always(() => {
            this.trigger(_Events.GIFT_CARD_PAYMENT_FINISHED);
          });
      }
    }
  }
}
